/* =============================================================================
   ONLI.Shop Theme — predictive-search.js
   Loaded only on pages that need it. Depends on global.js (window.theme.utils).
   ============================================================================= */

(() => {
  'use strict';
  const { debounce, parseHTML } = window.theme.utils;

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-search]');
      this.reset = this.querySelector('[type="reset"]');
      this.status = this.querySelector('.predictive-search-status');
      this.form = this.querySelector('form');
      this.cache = {};
      this.disabled = this.hasAttribute('data-disabled');
      if (!this.input) return;
      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
      this.input.addEventListener('focus', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      if (this.reset) {
        this.reset.addEventListener('click', () => {
          this.input.value = '';
          this.input.focus();
          this.close();
        });
      }
      this.addEventListener('click', (event) => {
        if (event.target.closest('.predictive-search__view-all')) {
          if (this.form.requestSubmit) this.form.requestSubmit();
          else this.form.submit();
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onInput() {
      const query = this.getQuery();
      if (this.reset) this.reset.classList.toggle('hidden', !query);
      if (this.disabled || !query.length) {
        this.close();
        return;
      }
      this.getSearchResults(query);
    }

    onKeydown(event) {
      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
        event.preventDefault();
        const next = event.code === 'ArrowDown' ? Math.min(current + 1, options.length - 1) : Math.max(current - 1, 0);
        options.forEach((o, i) => o.setAttribute('aria-selected', i === next ? 'true' : 'false'));
        this.input.setAttribute('aria-activedescendant', options[next].id || '');
        options[next].scrollIntoView({ block: 'nearest' });
      } else if (event.code === 'Enter' && current >= 0) {
        const selected = options[current];
        const link = selected.matches('a') ? selected : selected.querySelector('a');
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    }

    async getSearchResults(query) {
      const key = query.toLowerCase();
      if (this.cache[key]) {
        this.render(this.cache[key]);
        return;
      }
      this.setLoading(true);
      if (this.status && this.dataset.loadingText) this.status.textContent = this.dataset.loadingText;
      const url = `${window.theme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(response.status);
        const text = await response.text();
        const doc = parseHTML(text);
        const inner = doc.querySelector('#shopify-section-predictive-search');
        const html = inner ? inner.innerHTML : text;
        this.cache[key] = html;
        if (this.getQuery().toLowerCase() === key) this.render(html);
      } catch (error) {
        this.close();
      } finally {
        this.setLoading(false);
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.results.removeAttribute('hidden');
      this.input.setAttribute('aria-expanded', 'true');
      this.input.removeAttribute('aria-activedescendant'); // the previously highlighted option no longer exists
      const count = this.results.querySelectorAll('[role="option"]').length;
      if (this.status) {
        const template = count === 1 ? this.status.dataset.statusOne : this.status.dataset.statusOther;
        this.status.textContent = (template || '[count] results').replace('[count]', count);
      }
    }

    setLoading(on) {
      this.results.toggleAttribute('data-loading', on);
    }

    close() {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (this.status) this.status.textContent = '';
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

})();
